import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { IDynamicElement } from '../../../_shared/models/dynamic/dynamic-element.model';
import { ImageDataDb } from '../../../_shared/data/image-data-db.data';
import { IMediaCollage } from '../../../_shared/models/media/media-collage.model';
import { IMediaItem } from '../../../_shared/models/media/media-item.model';
import { MediaImage } from '../../../_shared/models/media/media-image.model';
import { MediaCollectionService } from '../media-collection.service';
import { ImageData } from '../../../_shared/models/image/image-data.model';
import { ContainerComponent } from '../../../container/container.component';
import { ImageItemComponent } from '../../media-item/image-item/image-item.component';
import { MediaCollectionModalComponent } from '../media-collection-modal/media-collection-modal.component';
import { PlayButtonComponent } from '../../../button/play-button/play-button.component';

type Val = 2 | 3 | 4 | 5;

const COLLAGE_IMAGE_DATA = [
    [ImageDataDb.media.collage.twoTwo, ImageDataDb.media.collage.twoTwo],
    [ImageDataDb.media.collage.twoTwo, ImageDataDb.media.collage.twoOne, ImageDataDb.media.collage.twoOne],
    [
        ImageDataDb.media.collage.twoTwo,
        ImageDataDb.media.collage.oneOne,
        ImageDataDb.media.collage.oneOne,
        ImageDataDb.media.collage.twoOne,
    ],
    [
        ImageDataDb.media.collage.twoTwo,
        ImageDataDb.media.collage.oneOne,
        ImageDataDb.media.collage.oneOne,
        ImageDataDb.media.collage.oneOne,
        ImageDataDb.media.collage.oneOne,
    ],
] satisfies (ImageData | undefined)[][];

@Component({
    selector: 'sirius-media-collage',
    templateUrl: './media-collage.component.html',
    styleUrls: ['./media-collage.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ContainerComponent, ImageItemComponent, MediaCollectionModalComponent, PlayButtonComponent],
    host: {
        'class': 'sir-media-collage',
        '[class.has-two-items]': 'val() === 2',
        '[class.has-three-items]': 'val() === 3',
        '[class.has-four-items]': 'val() === 4',
        '[class.has-five-items]': 'val() === 5',
        '[attr.data-item-count]': 'val()',
    },
})
export class MediaCollageComponent implements OnInit, IDynamicElement {
    private readonly mediaCollectionService = inject(MediaCollectionService);

    readonly items = input<IMediaItem[]>([]);
    readonly showDescription = input(false);

    config?: IMediaCollage;
    readonly hoveredIndex = signal<number | null>(null);
    readonly index = signal(0);
    readonly open = signal(false);

    private readonly configItems = signal<IMediaItem[] | undefined>(undefined);

    readonly resolvedItems = computed(() => this.configItems() ?? this.items());
    readonly val = computed<Val>(() => this.getCollageValue(this.resolvedItems().length));
    readonly thumbnails = computed<MediaImage[]>(() => this.mediaCollectionService.createThumbnails(this.resolvedItems()));

    ngOnInit(): void {
        this.configItems.set(this.config?.items);
    }

    getImageData(index: number): ImageData | undefined {
        return COLLAGE_IMAGE_DATA[this.val() - 2]?.[index];
    }

    onClose(): void {
        this.open.set(false);
    }

    onClick(index: number): void {
        this.index.set(index);
        this.open.set(true);
    }

    onMouseEnter(index: number): void {
        this.hoveredIndex.set(index);
    }

    onMouseLeave(): void {
        this.hoveredIndex.set(null);
    }

    private getCollageValue(itemCount: number): Val {
        if (itemCount <= 2) return 2;
        if (itemCount >= 5) return 5;

        return itemCount as Val;
    }
}
